import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Grid, Button, Card, Typography, Box, CircularProgress, Container, Select, MenuItem } from '@mui/material';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import DeleteIcon from '@mui/icons-material/Delete';
import Whatshot from '@mui/icons-material/Whatshot';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { toWapi, get, post } from '../../util/api';
import RiskDetails from '../../components/RiskLabel';
import SystemStatusBar from '../../components/StatusBar';
import _ from 'lodash';

const Warmingup = ({ product }) => {
	const [phoneId, setPhoneId] = useState('');
	const [selectBoxOptions, setSelectBoxOptions] = useState([]);
	const [warmingData, setWarmingData] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState(null);
	const [isSystemActive, setIsSystemActive] = useState(false);
	const [phoneDetailsMap, setPhoneDetailsMap] = useState({});
	const [riskFactorsMap, setRiskFactorsMap] = useState({});
	const [expandedCard, setExpandedCard] = useState(null);
	const [isInitialLoading, setIsInitialLoading] = useState(true);

	/* const mockCalculateRisk = (phoneId, number) => ({
		success: true,
		result: {
			phone_id: phoneId,
			number,
			data: {
				life_time: Math.floor(Math.random() * 3) + 1,
				riskFactorContacts: Math.floor(Math.random() * 3) + 1,
				riskFactorResponse: Math.floor(Math.random() * 3) + 1,
				riskFactor: Math.floor(Math.random() * 3) + 1,
			},
			created_at: new Date().toISOString(),
		},
	}); */

	const getRiskLabel = (riskFactor) => {
		switch (riskFactor) {
			case 1:
				return { label: 'Critical Risk', color: '#ef4444' };
			case 2:
				return { label: 'Moderate Risk', color: '#f59e0b' };
			case 3:
				return { label: 'Good Indicator', color: '#22c55e' };
			default:
				return { label: 'Unknown', color: '#6b7280' };
		}
	};

	const fetchRiskFactors = async (phones) => {
		const {
			id,
			settings: { wapi_token },
		} = product;
		try {
			const riskPromises = phones.map((phone) => toWapi(`/api/${id}/${phone.mainID}/calculateRiskOfBlock`, wapi_token).then((res) => res.data));
			const riskResults = await Promise.all(riskPromises);
			const newRiskFactorsMap = {};

			riskResults.forEach((response, index) => {
				if (response.success) {
					newRiskFactorsMap[phones[index].mainID] = response.result;
				}
			});

			setRiskFactorsMap(newRiskFactorsMap);
		} catch (error) {
			console.error('Error fetching risk factors:', error);
		}
	};

	const handleCardClick = (phoneId) => {
		setExpandedCard(expandedCard === phoneId ? null : phoneId);
	};


	const fetchWarmingData = async () => {
		setError(null);
		try {
			const response = await get('/warming/listWarming');
			if (response.success && Array.isArray(response.data)) {
				const warmingDataWithDetails = response.data.map((item) => ({
					...item,
				}));
				let first = warmingDataWithDetails[0];
				if (first) {
					setIsSystemActive(first.status === 'active');
				}
				setWarmingData(warmingDataWithDetails);

				await fetchRiskFactors(warmingDataWithDetails);
			} else {
				setWarmingData([]);
			}
		} catch (error) {
			console.error('Error fetching warming data:', error);
			setError('Failed to fetch data: ' + (error.message || 'Unknown error'));
		} finally {
			setIsLoading(false);
		}
	};
	const fetchInitialData = async () => {
		setIsInitialLoading(true);
		try {
			// First fetch phone list
			const {
				id,
				settings: { wapi_token },
			} = product;

			const phoneListData = await toWapi(`/api/${id}/listPhones`, wapi_token);
			if (phoneListData.status === 200 && Array.isArray(phoneListData.data)) {
				const detailsMap = {};
				phoneListData.data.forEach((phone) => {
					detailsMap[phone.id] = {
						...phone,
						id: phone.id,
						status: phone.status,
						number: phone.number,
						product: phone.product,
						sandbox: (phone.data && phone.data.sandbox) || false,
					};
				});
				setPhoneDetailsMap(detailsMap);
				setSelectBoxOptions(phoneListData.data);
			}

			// Then fetch warming data
			const warmingResponse = await get('/warming/listWarming');
			if (warmingResponse.success && Array.isArray(warmingResponse.data)) {
				const warmingDataWithDetails = warmingResponse.data.map((item) => ({
					...item,
				}));
				let first = warmingDataWithDetails[0];
				if (first) {
					setIsSystemActive(first.status === 'active');
				}
				setWarmingData(warmingDataWithDetails);

				// Finally fetch risk factors
				await fetchRiskFactors(warmingDataWithDetails);
			} else {
				setWarmingData([]);
			}
		} catch (error) {
			console.error('Error fetching initial data:', error);
			setError('Failed to load initial data: ' + (error.message || 'Unknown error'));
		} finally {
			setIsInitialLoading(false);
			setIsLoading(false);
		}
	};

	useEffect(() => {
		fetchInitialData();
	}, [product]);
	if (isInitialLoading) {
		return (
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					minHeight: '80vh',
					flexDirection: 'column',
					gap: 2,
				}}
			>
				<CircularProgress size={40} />
				<Typography color="textSecondary">Loading data...</Typography>
			</Box>
		);
	}
	const handleAddWarmingData = async () => {
		if (!phoneId) {
			setError('Please select a phone');
			return;
		}

		setIsSubmitting(true);
		setError(null);

		try {
			const response = await post('/warming/createWarming', {
				phone_id: phoneId,
				status: 'active',
				product_id: product.id,
				product_uid: product.uid,
				token: product.settings.wapi_token,
			});

			if (response.data.success) {
				setPhoneId('');
				setError('Phone added successfully to warming pool');
				setTimeout(() => {
					fetchWarmingData();
				}, 1000);
			} else {
				setError('Failed to add warming data: ' + (response.message || 'Unknown error'));
			}
		} catch (error) {
			console.error('Error adding warming data:', error);
			setError('Failed to add warming data: ' + (error.message || 'Unknown error'));
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleDeletePhone = async (id) => {
		try {
			const response = await get(`/warming/deleteWarming?id=${id}`);
			if (response.success) {
				setError('Phone removed from warming pool');
				await fetchWarmingData();
			} else {
				setError('Failed to remove phone: ' + (response.message || 'Unknown error'));
			}
		} catch (error) {
			console.error('Error removing phone:', error);
			setError('Failed to remove phone: ' + (error.message || 'Unknown error'));
		}
	};

	const handleSystemToggle = async () => {
		try {
			const newStatus = !isSystemActive;
			await post('/warming/updateAllWarmingStatus', {
				status: newStatus ? 'active' : 'disabled',
				product_id: product.id,
			});
			setIsSystemActive(newStatus);
			await fetchWarmingData();
		} catch (error) {
			console.error('Error toggling system status:', error);
			setError('Failed to toggle system status');
		}
	};

	return (
		<Container maxWidth="xl" sx={{ mt: 2, px: { xs: 1, sm: 2, md: 3 }, width: '100%', maxWidth: '1600px' }}>
			<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
				<Typography variant="h4">Warming Up Numbers</Typography>
				<SystemStatusBar isActive={isSystemActive} onToggle={handleSystemToggle} />
				{/* <Button variant="contained" color={isSystemActive ? 'error' : 'primary'} onClick={handleSystemToggle} sx={{ px: 4 }}>
					{isSystemActive ? 'Stop' : 'Start'}
				</Button> */}
			</Box>

			<Grid container spacing={3}>
				<Grid item xs={12} md={4}>
					<Card sx={{ p: 3, bgcolor: 'primary.main', color: 'white' }}>
						<Typography variant="h6" gutterBottom>
							Automatic warm-up module
						</Typography>
						<Typography gutterBottom sx={{ mb: 3 }}>
							Select channels to add to the warming pool
						</Typography>

						<Select
							fullWidth
							sx={{
								mt: 1.8,
								bgcolor: 'rgba(255,255,255,0.1)',
								'.MuiSelect-icon': { color: 'white' },
								color: 'white',
							}}
							value={phoneId}
							onChange={(e) => setPhoneId(e.target.value)}
							displayEmpty
							startAdornment={<PhoneAndroidIcon sx={{ mr: 1, color: 'white' }} />}
						>
							<MenuItem value="">Select Phone</MenuItem>
							{selectBoxOptions.map((option) => (
								<MenuItem key={option.id} value={option.id}>
									{option.id} {option.data && option.data.sandbox ? '(Sandbox)' : ''}
								</MenuItem>
							))}
						</Select>

						<Button
							fullWidth
							variant="contained"
							sx={{
								mt: 2,
								bgcolor: 'rgba(255,255,255,0.2)',
								'&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
							}}
							onClick={handleAddWarmingData}
							startIcon={<Whatshot />}
							disabled={isSubmitting}
						>
							{isSubmitting ? <CircularProgress size={24} /> : 'Add Phone'}
						</Button>

						{error && <Typography sx={{ mt: 2, color: 'error.light' }}>{error}</Typography>}
					</Card>
				</Grid>

				<Grid item xs={12} md={8}>
					<Grid container spacing={2}>
						{isLoading ? (
							<Grid item xs={12} sx={{ textAlign: 'center' }}>
								<CircularProgress />
							</Grid>
						) : warmingData.length === 0 ? (
							<Grid item xs={12}>
								<Typography>No channels in warming pool. Add one to get started.</Typography>
							</Grid>
						) : (
							warmingData.map((phone) => (
								<Grid item xs={12} md={6} key={phone.id}>
									<Card
										sx={{
											p: 2,
											cursor: 'pointer',
											'&:hover': { bgcolor: 'rgba(0, 0, 0, 0.02)' },
										}}
										onClick={() => handleCardClick(phone.mainID)}
									>
										<Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
											<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
												<PhoneAndroidIcon />
												<Box>
													<Typography variant="h6">{phone.mainID}</Typography>
													<Typography variant="caption" color="textSecondary">
														{phoneDetailsMap[phone.mainID] && phoneDetailsMap[phone.mainID].number}
													</Typography>
												</Box>
											</Box>
											<Box sx={{ display: 'flex', gap: 1, alignItems: 'start' }}>
												<Typography
													variant="caption"
													sx={{
														px: 1,
														py: 0.5,
														borderRadius: 1,
														bgcolor:
															phoneDetailsMap[phone.mainID] &&
															phoneDetailsMap[phone.mainID].data &&
															phoneDetailsMap[phone.mainID].data.sandbox
																? 'info.light'
																: 'success.light',
														color: 'white',
													}}
												>
													{phoneDetailsMap[phone.mainID] &&
													phoneDetailsMap[phone.mainID].data &&
													phoneDetailsMap[phone.mainID].data.sandbox
														? 'SANDBOX'
														: 'LIVE'}
												</Typography>
												<Button
													size="small"
													color="error"
													onClick={(e) => {
														e.stopPropagation();
														handleDeletePhone(phone.id);
													}}
												>
													<DeleteIcon />
												</Button>
												{expandedCard === phone.mainID ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
											</Box>
										</Box>

										<Grid container spacing={2}>
											<Grid item xs={6}>
												<Typography variant="caption" color="textSecondary">
													Risk Factor
												</Typography>
												<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
													<Typography
														sx={{
															px: 2,
															py: 0.5,
															borderRadius: 1,
															bgcolor: getRiskLabel(
																riskFactorsMap[phone.mainID] &&
																	riskFactorsMap[phone.mainID].data &&
																	riskFactorsMap[phone.mainID].data.riskFactor
															).color,
															color: 'white',
															fontSize: '0.875rem',
														}}
													>
														{
															getRiskLabel(
																riskFactorsMap[phone.mainID] &&
																	riskFactorsMap[phone.mainID].data &&
																	riskFactorsMap[phone.mainID].data.riskFactor
															).label
														}
													</Typography>
												</Box>
											</Grid>
											<Grid item xs={6}>
												<Typography variant="caption" color="textSecondary">
													Status
												</Typography>
												<Typography>{(phoneDetailsMap[phone.mainID] && phoneDetailsMap[phone.mainID].status) || 'Unknown'}</Typography>
											</Grid>
										</Grid>

										{expandedCard === phone.mainID && (
											<RiskDetails riskFactorsMap={riskFactorsMap} phoneId={phone.mainID} getRiskLabel={getRiskLabel} />
										)}
									</Card>
								</Grid>
							))
						)}
					</Grid>
				</Grid>
			</Grid>
		</Container>
	);
};

const stateToProps = (state) => ({
	access: state.access,
	location: state.router.location,
	product: state.product.maytapi,
});

export default connect(stateToProps)(Warmingup);
